import { CardContainer } from 'finara-components';

import type { ProductSummary } from '@/types/analytics';

interface ProductMetric {
  label: string;
  value: string;
  isPositive?: boolean;
}

interface ProductData {
  title: string;
  colorClass: string;
  bgColorClass: string;
  metrics: ProductMetric[];
}

interface GlobalProductSummaryProps {
  title?: string;
  productSummary?: ProductSummary;
}

const ProductCard: React.FC<{ product: ProductData }> = ({ product }) => {
  return (
    <CardContainer className={`!p-[14px] ${product.bgColorClass}`}>
      <div className="flex items-center mb-4">
        <div className={`w-3 h-3 ${product.colorClass} rounded-full mr-2`}></div>
        <h3 className="text-sm font-semibold text-brand-white">{product.title}</h3>
      </div>
      {product.metrics.map((metric, index) => (
        <div key={index} className={index === 0 ? 'mt-[10.5px]' : 'mt-[7px]'}>
          <div className="text-[12.25px] text-muted">{metric.label}:</div>
          <div
            className={`text-sm font-${index === 0 ? 'bold' : 'semibold'} ${
              metric.isPositive ? 'text-green-400' : 'text-brand-white'
            }`}
          >
            {metric.value}
          </div>
        </div>
      ))}
    </CardContainer>
  );
};

const formatCurrency = (amount: number): string => {
  if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
  if (amount >= 1000) return `$${(amount / 1000).toFixed(1)}K`;
  return `$${amount.toLocaleString()}`;
};

export const GlobalProductSummary: React.FC<GlobalProductSummaryProps> = ({
  title = 'Resumen Global por Producto',
  productSummary,
}) => {
  if (!productSummary) {
    return (
      <CardContainer title={title}>
        <div className="text-center py-8 text-muted">No hay datos disponibles</div>
      </CardContainer>
    );
  }

  const products: ProductData[] = [
    {
      title: 'SPEI IN',
      colorClass: 'bg-blue-500',
      bgColorClass: 'bg-blue-50/30',
      metrics: [
        { label: 'Total Mes', value: formatCurrency(productSummary.speiIn.totalMonth) },
        { label: 'Operaciones', value: productSummary.speiIn.operations.toLocaleString() },
      ],
    },
    {
      title: 'SPEI OUT',
      colorClass: 'bg-red-500',
      bgColorClass: 'bg-red-50/30',
      metrics: [
        { label: 'Total Mes', value: formatCurrency(productSummary.speiOut.totalMonth) },
        { label: 'Operaciones', value: productSummary.speiOut.operations.toLocaleString() },
      ],
    },
    {
      title: 'Total en Cuentas',
      colorClass: 'bg-emerald-500',
      bgColorClass: 'bg-emerald-50/30',
      metrics: [
        { label: 'Saldo Total', value: formatCurrency(productSummary.totalAccounts.totalBalance) },
        { label: 'Cuentas', value: productSummary.totalAccounts.accountsCount.toLocaleString() },
      ],
    },
    {
      title: 'Nómina',
      colorClass: 'bg-green-500',
      bgColorClass: 'bg-green-50/30',
      metrics: [
        { label: 'Total Mes', value: formatCurrency(productSummary.payroll.totalMonth) },
        { label: 'Empleados', value: productSummary.payroll.employeesCount.toLocaleString() },
      ],
    },
    {
      title: 'Adelantos',
      colorClass: 'bg-orange-500',
      bgColorClass: 'bg-orange-50/30',
      metrics: [
        { label: 'Activos', value: formatCurrency(productSummary.advances.active) },
        { label: 'Recuperación', value: `${productSummary.advances.recoveryRate.toFixed(1)}%` },
      ],
    },
    {
      title: 'Caja de Ahorro',
      colorClass: 'bg-indigo-600',
      bgColorClass: 'bg-indigo-50/30',
      metrics: [
        { label: 'Total Invertido', value: formatCurrency(productSummary.savings.totalInvested) },
        {
          label: 'Rendimientos',
          value: `+${formatCurrency(productSummary.savings.returns)}`,
          isPositive: true,
        },
      ],
    },
  ];

  return (
    <CardContainer title={title}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
        {products.map((product, index) => (
          <ProductCard key={index} product={product} />
        ))}
      </div>
    </CardContainer>
  );
};
